import { entryPath } from "../lib/appEntryLink"
import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import SignInForm from "../forms/SignInForm"
import * as Sentry from "@sentry/browser"

import utilsCss from "../components/utilsCss.module.css"
import css from "./SignIn.module.css"
import classNames from "classnames"
import {
  connect,
  resetCredentialsAndConnect,
  setXmppOnWindow,
} from "../stream/xmppClient"
import { useNavigate } from "react-router-dom"
import { useAppStore } from "../reducers/hooks"
import Cookies from "universal-cookie"
import { Callout } from "@blueprintjs/core"
import { useTranslation } from "react-i18next"
import posthog from "posthog-js"
import { JID } from "@xmpp/jid"
import { acquireTabSlot } from "../stream/tabSlots"
import { rememberAccount } from "../lib/rememberedAccount"

const TWO_WEEKS = 12096e5

export const setupAnalytics = (userAuth: Types.Api.UserAuth) => {
  window.performance.mark("SetupAnalytics")
  Sentry.setUser({ email: userAuth.email })
  posthog.identify(userAuth.email, { vhost: userAuth.email.split("@").at(-1) })
  window.analytics.identify(userAuth.email, {
    email: userAuth.email,
    appVersion: window.APP_VERSION,
  })
}

export const getUserAuthFromCookies = () => {
  const cookieOptions = {
    path: "/",
    expires: new Date(Date.now() + TWO_WEEKS),
    domain: window.location.hostname,
    secure: window.location.hostname !== "localhost",
  }

  const cookies = new Cookies(null, cookieOptions)
  const email = cookies.get("email") as string | undefined
  const password = cookies.get("password") as string | undefined

  if (email && password) {
    return { email, password }
  }

  return null
}

export const hasUserAuthCookies = () => {
  return !!getUserAuthFromCookies()
}

export const clearAuthCookies = () => {
  const cookies = new Cookies(null, { path: "/" })
  cookies.remove("email", { path: "/", domain: window.location.hostname })
  cookies.remove("password", { path: "/", domain: window.location.hostname })
}

export const configureWindowXmpp = () => {
  const userAuth = getUserAuthFromCookies()
  if (!userAuth) {
    throw new Error("No cookies with auth information")
  }

  return setXmppOnWindow(userAuth)
}

export type SignInWithCookiesResult =
  | { kind: "ok"; jid: JID }
  | { kind: "not-authorized" }
  | { kind: "failed"; reason: string }

export const attemptToSignInWithCookies =
  async (): Promise<SignInWithCookiesResult> => {
    window.performance.mark("AttemptToSignInWithCookie")
    const userAuth = getUserAuthFromCookies()
    if (!userAuth) {
      return { kind: "not-authorized" }
    }

    Sentry.setUser({ email: userAuth.email })

    const result = await connect(window.store)

    if (result.connected) {
      setupAnalytics(userAuth)
      rememberAccount(userAuth.email)
      return { kind: "ok", jid: result.jid }
    }

    if (result.error === "not-authorized") {
      return { kind: "not-authorized" }
    }

    return { kind: "failed", reason: result.error ?? "unknown" }
  }

const SignIn = () => {
  const { t } = useTranslation()
  const [isFailed, setIsFailed] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  // A ref, not the state: a double click fires the second submit before React
  // re-renders with the disabled button.
  const isSubmittingRef = useRef(false)
  const navigate = useNavigate()
  const store = useAppStore()
  const cookieOptions = useMemo(() => {
    return {
      path: "/",
      expires: new Date(Date.now() + TWO_WEEKS),
      domain: window.location.hostname,
      secure: import.meta.env.REACT_APP_ENV_NAME !== "development",
    }
  }, [])

  const handleOnSubmit = useCallback(
    async (userAuth: Types.Api.UserAuth) => {
      if (isSubmittingRef.current) return
      isSubmittingRef.current = true
      setIsSubmitting(true)
      setIsFailed(false)

      const finishSubmitting = () => {
        isSubmittingRef.current = false
        setIsSubmitting(false)
      }

      // Signing in also opens an XMPP connection, so it obeys the tab limit too.
      if (!(await acquireTabSlot())) {
        finishSubmitting()
        navigate(entryPath("/too-many-tabs", window.location.search), {
          replace: true,
        })
        return
      }

      resetCredentialsAndConnect(userAuth, store)
        .then(({ connected: isConnected }) => {
          if (isConnected) {
            const cookies = new Cookies(null, cookieOptions)
            cookies.set("email", userAuth.email)
            cookies.set("password", userAuth.password)
            setupAnalytics(userAuth)
            rememberAccount(userAuth.email)
            window.analytics.track("LoggedIn")
            navigate(entryPath("/", window.location.search), {
              replace: true,
            })
          } else {
            setIsFailed(true)
          }
        })
        .catch(() => {
          setIsFailed(true)
        })
        .finally(finishSubmitting)
    },
    [store, navigate, cookieOptions],
  )

  useLayoutEffect(() => {
    if (window.XMPP && window.XMPP.status === "online") {
      navigate(entryPath("/", window.location.search), { replace: true })
    }
  }, [navigate, store])

  return (
    <div className={classNames(css.signInForm, utilsCss.center)}>
      {isFailed && <Callout intent="danger">{t("app.signIn.invalid")}</Callout>}
      {<SignInForm onSubmit={handleOnSubmit} isSubmitting={isSubmitting} />}
    </div>
  )
}

export default SignIn
